import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import { 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  Target,
  AlertCircle,
  Plus,
  Trash2,
  Calendar,
  Sparkles,
  TrendingUp,
  Lightbulb,
  Loader2,
  Goal
} from 'lucide-react'
import { aiService, SMARTAnalysisResult, StrategicAlignmentSuggestion, CompleteCascadePath } from '@/services/aiService'
import { useKPAFramework } from '@/hooks/useKPAFramework'
import { StrategicContextSelector, type StrategicContext } from '@/components/strategic/StrategicContextSelector'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import { 
  useLookupStrategicObjectives, 
  useLookupStrategicInitiatives, 
  useLookupOperationalInitiatives 
} from '@/hooks/useStrategicFramework'

interface KPI {
  id: string
  title: string
  description: string
  target: string
  unit: string
  frequency: 'bi-annually' | 'annually' | 'quarterly' | 'monthly'
  weight: number
  // Enterprise Measurement Configuration
  measurementDirection: 'higher_is_better' | 'lower_is_better'
  measurementMode: 'numeric' | 'percentage' | 'rating' | 'binary'
}

interface ObjectiveData {
  id: string
  title: string
  description: string
  kpa: string
  kpaName: string
  pillar: string
  startDate: string
  endDate: string
  kpis: KPI[]
  // Strategic context (L1-L3) - optional
  strategic_objective_id?: number | null
  strategic_initiative_id?: number | null
  operational_initiative_id?: number | null
  // Real AI SMART analysis (persisted so it isn't discarded on save)
  smart_analysis?: SMARTAnalysisResult | null
}

interface ImprovedObjectiveWizardProps {
  onSave: (objectives: ObjectiveData[]) => void
  onCancel: () => void
  /** The performance year the objective is for: its dates start as that whole year and stay
   *  inside it (HR UAT list #27). Defaults to this year. */
  year?: number
}

// KPAs are now fetched dynamically from the backend via useKPAFramework hook

const steps = [
  { id: 1, name: 'Basic Information', icon: Goal },
  { id: 2, name: 'Timeline', icon: Calendar },
  { id: 3, name: 'KPIs', icon: Target },
  { id: 4, name: 'SMART Analysis', icon: Sparkles },
  { id: 5, name: 'Review', icon: Check }
]

export default function ImprovedObjectiveWizard({ onSave, onCancel, year }: ImprovedObjectiveWizardProps) {
  const { kpaOptions, isLoading: kpasLoading, getKPAByName } = useKPAFramework()
  
  // Strategic framework lookup for L1-L3 names in SMART analysis
  const { data: strategicObjectivesLookup = [] } = useLookupStrategicObjectives()
  const { data: strategicInitiativesLookup = [] } = useLookupStrategicInitiatives()
  const { data: operationalInitiativesLookup = [] } = useLookupOperationalInitiatives()
  
  const [currentStep, setCurrentStep] = useState(0)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [smartAnalysis, setSmartAnalysis] = useState<SMARTAnalysisResult | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [strategicSuggestions, setStrategicSuggestions] = useState<StrategicAlignmentSuggestion[]>([])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Form data
  const currentYear = year ?? new Date().getFullYear()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    kpa: '',
    kpaName: '',
    pillar: '',
    startDate: `${currentYear}-01-01`,
    endDate: `${currentYear}-12-31`,
    kpis: [] as KPI[],
    // Strategic context (L1-L3) - optional
    strategic_objective_id: null as number | null,
    strategic_initiative_id: null as number | null,
    operational_initiative_id: null as number | null
  })

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const updateKPA = (kpaId: string) => {
    const selectedKPA = kpaOptions.find(k => k.id === kpaId)
    if (selectedKPA) {
      setFormData(prev => ({
        ...prev,
        kpa: kpaId,
        kpaName: selectedKPA.name,
        pillar: selectedKPA.pillar
      }))
      if (errors.kpa) {
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors.kpa
          return newErrors
        })
      }
    }
  }

  const addKPI = () => {
    const newKPI: KPI = {
      id: `kpi-${Date.now()}`,
      title: '',
      description: '',
      target: '',
      unit: '',
      frequency: 'bi-annually',
      weight: 100,
      measurementDirection: 'higher_is_better',
      measurementMode: 'numeric'
    }
    setFormData(prev => ({ ...prev, kpis: [...prev.kpis, newKPI] }))
  }

  const removeKPI = (id: string) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.filter(kpi => kpi.id !== id)
    }))
    // Clear any errors for this KPI
    setErrors(prev => {
      const newErrors = { ...prev }
      const kpiIndex = formData.kpis.findIndex(k => k.id === id)
      delete newErrors[`kpi_${kpiIndex}_title`]
      delete newErrors[`kpi_${kpiIndex}_target`]
      return newErrors
    })
  }

  const updateKPI = (id: string, field: keyof KPI, value: any) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.map(kpi =>
        kpi.id === id ? { ...kpi, [field]: value } : kpi
      )
    }))
    // Clear error for this KPI field
    const kpiIndex = formData.kpis.findIndex(k => k.id === id)
    const errorKey = `kpi_${kpiIndex}_${field}`
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
    }
  }

  const getStrategicSuggestions = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      return
    }

    setIsLoadingSuggestions(true)
    try {
      const suggestions = await aiService.getStrategicSuggestions(
        formData.title,
        formData.description,
        formData.kpaName
      )
      if (import.meta.env.DEV) console.log('Received strategic suggestions:', suggestions)
      setStrategicSuggestions(suggestions)
      setShowSuggestions(true)
    } catch (error) {
      console.error('Failed to get strategic suggestions:', error)
    } finally {
      setIsLoadingSuggestions(false)
    }
  }

  const applyCompleteCascadePath = (path: CompleteCascadePath) => {
    if (import.meta.env.DEV) console.log('Applying complete cascade path:', path)
    
    setFormData(prev => ({
      ...prev,
      strategic_objective_id: path.strategic_objective_id,
      strategic_initiative_id: path.strategic_initiative_id,
      operational_initiative_id: path.operational_initiative_id
    }))
    // Keep suggestions panel open so users can explore other paths
    
    if (import.meta.env.DEV) console.log(`✓ Applied complete path: ${path.strategic_objective_code} → ${path.strategic_initiative_code} → ${path.operational_initiative_code}`)
  }

  const applyStrategicSuggestion = (suggestion: StrategicAlignmentSuggestion) => {
    if (import.meta.env.DEV) console.log('Applying strategic suggestion:', suggestion)
    console.log('Complete paths available:', suggestion.complete_paths?.length || 0)
    
    // Check if complete_paths exists and has data
    if (!suggestion.complete_paths || suggestion.complete_paths.length === 0) {
      console.warn('⚠️ No complete_paths in suggestion - only L1 will be applied')
      if (import.meta.env.DEV) console.log('Suggestion structure:', JSON.stringify(suggestion, null, 2))
      
      // Only apply L1 if no complete paths available
      setFormData(prev => ({
        ...prev,
        strategic_objective_id: suggestion.strategic_objective_id,
        strategic_initiative_id: null,
        operational_initiative_id: null
      }))
      if (import.meta.env.DEV) console.log(`✓ Applied ${suggestion.strategic_objective_name}. WARNING: L2/L3 not available - please click a specific path or re-generate suggestions.`)
      return
    }
    
    // Use AI-recommended path if available, otherwise first path
    const recommendedPath = suggestion.complete_paths.find(p => p.is_recommended)
    const pathToApply = recommendedPath || suggestion.complete_paths[0]
    
    applyCompleteCascadePath(pathToApply)
    if (recommendedPath) {
      if (import.meta.env.DEV) console.log(`✓ Applied AI-recommended path with ${Math.round((recommendedPath.confidence || 0) * 100)}% confidence`)
    } else {
      if (import.meta.env.DEV) console.log(`✓ Applied first available path`)
    }
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    if (step === 0) {
      // Validate basic information
      if (!formData.title.trim()) {
        newErrors.title = 'Objective title is required'
      }
      if (!formData.description.trim()) {
        newErrors.description = 'Description is required'
      } else if (formData.description.trim().length < 10) {
        newErrors.description = 'Description must be at least 10 characters'
      }
      if (!formData.kpa) {
        newErrors.kpa = 'Please select a KPA'
      }
    }

    if (step === 1) {
      // Validate timeline
      if (!formData.startDate) {
        newErrors.startDate = 'Start date is required'
      }
      if (!formData.endDate) {
        newErrors.endDate = 'End date is required'
      }
      if (formData.startDate && !formData.startDate.startsWith(`${currentYear}-`)) {
        newErrors.startDate = `This objective is for ${currentYear}: choose a date in ${currentYear}`
      }
      if (formData.endDate && !formData.endDate.startsWith(`${currentYear}-`)) {
        newErrors.endDate = `This objective is for ${currentYear}: choose a date in ${currentYear}`
      }
      if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
        newErrors.endDate = 'End date must be after start date'
      }
    }

    if (step === 2) {
      // Validate KPIs
      if (formData.kpis.length === 0) {
        newErrors.kpis = 'At least one KPI is required'
      }
      formData.kpis.forEach((kpi, index) => {
        if (!kpi.title.trim()) {
          newErrors[`kpi_${index}_title`] = 'KPI name is required'
        }
        if (!kpi.target.trim()) {
          newErrors[`kpi_${index}_target`] = 'Target value is required'
        }
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = async () => {
    if (validateStep(currentStep)) {
      // Trigger SMART analysis when moving to step 4 (SMART Analysis)
      if (currentStep === 2 && !smartAnalysis) {
        await performSmartAnalysis()
        // Only move to next step after analysis completes
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1))
      } else {
        // For other steps, move immediately
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1))
      }
    }
  }

  const performSmartAnalysis = async () => {
    setIsAnalyzing(true)
    setAnalysisError(null)
    
    try {
      const kpisForAnalysis = formData.kpis.map(kpi => ({
        name: kpi.title,
        description: kpi.description,
        target_value: kpi.target,
        unit: kpi.unit,
        measurement_type: kpi.measurementMode,
        measurement_direction: kpi.measurementDirection
      }))

      const selectedKPA = kpaOptions.find(k => k.id === formData.kpa)
      
      // Look up full L1-L3 strategic context names and descriptions
      const selectedL1 = formData.strategic_objective_id 
        ? strategicObjectivesLookup.find(o => o.id === formData.strategic_objective_id)
        : null
      const selectedL2 = formData.strategic_initiative_id
        ? strategicInitiativesLookup.find(i => i.id === formData.strategic_initiative_id)
        : null
      const selectedL3 = formData.operational_initiative_id
        ? operationalInitiativesLookup.find(o => o.id === formData.operational_initiative_id)
        : null

      const result = await aiService.analyzeSMART({
        title: formData.title,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate,
        kpis: kpisForAnalysis,
        // KPA context
        kpa_name: formData.kpaName || selectedKPA?.name || undefined,
        // Full L1-L3 strategic context with names (lookup types don't include descriptions)
        strategic_objective_id: formData.strategic_objective_id ?? undefined,
        strategic_objective: selectedL1 ? {
          code: selectedL1.code,
          name: selectedL1.display_name || selectedL1.name
        } : undefined,
        strategic_initiative_id: formData.strategic_initiative_id ?? undefined,
        strategic_initiative: selectedL2 ? {
          code: selectedL2.code,
          name: selectedL2.name
        } : undefined,
        operational_initiative_id: formData.operational_initiative_id ?? undefined,
        operational_initiative: selectedL3 ? {
          code: selectedL3.code,
          name: selectedL3.name
        } : undefined
      })

      setSmartAnalysis(result)
    } catch (error) {
      console.error('SMART analysis failed:', error)
      setAnalysisError('Failed to perform SMART analysis. You can continue without it.')
      // Still set a default analysis so user can proceed
      setSmartAnalysis(null)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const rerunAnalysis = () => {
    setSmartAnalysis(null)
    performSmartAnalysis()
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800'
    if (score >= 60) return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent'
    if (score >= 60) return 'Good'
    if (score >= 40) return 'Fair'
    return 'Needs Improvement'
  }

  const getScoreBorderColor = (score: number) => {
    if (score >= 80) return 'border-l-green-500'
    if (score >= 60) return 'border-l-yellow-500'
    return 'border-l-red-500'
  }

  const getScoreProgressColor = (score: number) => {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      const objective: ObjectiveData = {
        id: `obj-${Date.now()}`,
        ...formData,
        // Persist the real AI assessment the user just saw (instead of discarding it)
        smart_analysis: smartAnalysis
      }
      onSave([objective])
    }
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  }

  // Show loading state while KPAs are being fetched
  if (kpasLoading) {
    return (
      <div className="w-full max-w-3xl mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-heritage-blue mx-auto mb-4" />
              <p className="text-gray-600">Loading KPAs...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full max-w-3xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                  index < currentStep
                    ? 'bg-heritage-blue border-heritage-blue text-white'
                    : index === currentStep
                    ? 'border-heritage-blue text-heritage-blue'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {index < currentStep ? <Check className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
                </div>
                <span className={`mt-2 text-xs ${index <= currentStep ? 'text-heritage-blue font-medium' : 'text-gray-400'}`}>
                  {step.name}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-0.5 flex-1 mx-2 transition-all ${index < currentStep ? 'bg-heritage-blue' : 'bg-gray-300'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Card>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial="initial"
              animate="animate"
              exit="exit"
              variants={fadeInUp}
              transition={{ duration: 0.3 }}
            >
              {/* Step 1: Basic Information */}
              {currentStep === 0 && (
                <>
                  <CardHeader>
                    <CardTitle className="text-2xl text-heritage-blue">Basic Information</CardTitle>
                    <CardDescription>
                      Provide the core details for your objective
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">
                        Objective Title <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => updateField('title', e.target.value)}
                        placeholder="e.g., Improve Student Success Rates"
                        maxLength={500}
                        className={errors.title ? 'border-red-500' : ''}
                      />
                      <p className={`text-xs mt-1 ${formData.title.length > 450 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {formData.title.length}/500 characters
                      </p>
                      {errors.title && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-1 text-sm text-red-500"
                        >
                          <AlertCircle className="h-4 w-4" />
                          {errors.title}
                        </motion.div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">
                        Description <span className="text-red-500">*</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {/* OLD: ({formData.description.length}/10 min) */}
                          ({formData.description.length} chars, min 10)
                        </span>
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        placeholder="Describe what you want to achieve and why it matters..."
                        rows={4}
                        className={errors.description ? 'border-red-500' : ''}
                      />
                      {errors.description && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-1 text-sm text-red-500"
                        >
                          <AlertCircle className="h-4 w-4" />
                          {errors.description}
                        </motion.div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kpa">
                        Key Performance Area (KPA) <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.kpa}
                        onValueChange={updateKPA}
                      >
                        <SelectTrigger
                          id="kpa"
                          className={errors.kpa ? 'border-red-500' : ''}
                        >
                          <SelectValue placeholder="Select a KPA" />
                        </SelectTrigger>
                        <SelectContent>
                          {kpaOptions.map(kpa => (
                            <SelectItem key={kpa.id} value={kpa.id}>
                              {kpa.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.kpa && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-1 text-sm text-red-500"
                        >
                          <AlertCircle className="h-4 w-4" />
                          {errors.kpa}
                        </motion.div>
                      )}
                    </div>

                    {/* Strategic Context Selector (L1-L3) - Optional */}
                    <div className="space-y-2 pt-4 border-t border-gray-200">
                      <StrategicContextSelector
                        value={{
                          strategic_objective_id: formData.strategic_objective_id,
                          strategic_initiative_id: formData.strategic_initiative_id,
                          operational_initiative_id: formData.operational_initiative_id
                        }}
                        onChange={(context: StrategicContext) => {
                          setFormData(prev => ({
                            ...prev,
                            strategic_objective_id: context.strategic_objective_id ?? null,
                            strategic_initiative_id: context.strategic_initiative_id ?? null,
                            operational_initiative_id: context.operational_initiative_id ?? null
                          }))
                        }}
                      />
                      
                      {/* Helper text for L1-L3 strategic context */}
                      {formData.strategic_objective_id && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                          <p className="text-xs text-green-700">
                            <strong>✓ Strategic Path Applied:</strong> 
                            {formData.operational_initiative_id ? ' Complete L1→L2→L3 cascade selected!' : 
                             formData.strategic_initiative_id ? ' L1→L2 selected. Verify or select L3 above.' : 
                             ' L1 selected. Select L2 and L3 above.'}
                          </p>
                        </div>
                      )}
                      
                      {/* AI Strategic Suggestions Button */}
                      <div className="mt-4 flex items-center justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={getStrategicSuggestions}
                          disabled={isLoadingSuggestions || !formData.title || !formData.description}
                          className="gap-2"
                        >
                          {isLoadingSuggestions ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Getting AI Suggestions...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4" />
                              {/* OLD: Get AI Strategic Suggestions */}
                              Get AI Suggestions
                              {/* TODO: DEFERRED - Discuss if this should be mandatory or skippable */}
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Strategic Suggestions Display */}
                      {showSuggestions && strategicSuggestions.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-4 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">Suggested Alignment</h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowSuggestions(false)}
                              className="h-6 px-2 text-xs"
                            >
                              Dismiss
                            </Button>
                          </div>

                          {strategicSuggestions.map((suggestion, index) => (
                            <motion.div
                              key={suggestion.strategic_objective_id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="p-3 border rounded-lg hover:border-heritage-blue hover:bg-heritage-blue/5 cursor-pointer transition-all"
                              onClick={() => applyStrategicSuggestion(suggestion)}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="bg-heritage-blue/10 text-heritage-blue border-heritage-blue/20">
                                  {suggestion.strategic_objective_code}
                                </Badge>
                                <h5 className="font-semibold text-sm">{suggestion.strategic_objective_name}</h5>
                              </div>
                              <p className="text-xs text-gray-600">{suggestion.rationale}</p>
                              {suggestion.complete_paths && suggestion.complete_paths.length > 0 && (
                                <div className="mt-2 space-y-1" onClick={(e) => e.stopPropagation()}>
                                  {[...suggestion.complete_paths]
                                    .sort((a, b) => (b.is_recommended ? 1 : 0) - (a.is_recommended ? 1 : 0))
                                    .slice(0, 4)
                                    .map((path, idx) => (
                                    <div
                                      key={idx}
                                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-all text-xs ${
                                        path.is_recommended
                                          ? 'bg-amber-50 border border-amber-200 hover:bg-amber-100 font-medium text-gray-900'
                                          : 'bg-gray-50 hover:bg-heritage-blue/5 text-gray-600'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        applyCompleteCascadePath(path)
                                      }}
                                    >
                                      <span className="truncate flex-1">{path.operational_initiative_name}</span>
                                      {path.is_recommended && (
                                        <span className="text-[10px] text-amber-700 flex-shrink-0">Recommended</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  </CardContent>
                </>
              )}

              {/* Step 2: Timeline */}
              {currentStep === 1 && (
                <>
                  <CardHeader>
                    <CardTitle className="text-2xl text-heritage-blue">Timeline</CardTitle>
                    <CardDescription>
                      Set the timeframe for your objective
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="startDate">
                          Start Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="startDate"
                          type="date"
                          min={`${currentYear}-01-01`}
                          max={`${currentYear}-12-31`}
                          value={formData.startDate}
                          onChange={(e) => updateField('startDate', e.target.value)}
                          className={errors.startDate ? 'border-red-500' : ''}
                        />
                        {errors.startDate && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-1 text-sm text-red-500"
                          >
                            <AlertCircle className="h-4 w-4" />
                            {errors.startDate}
                          </motion.div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="endDate">
                          End Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="endDate"
                          type="date"
                          min={`${currentYear}-01-01`}
                          max={`${currentYear}-12-31`}
                          value={formData.endDate}
                          onChange={(e) => updateField('endDate', e.target.value)}
                          className={errors.endDate ? 'border-red-500' : ''}
                        />
                        {errors.endDate && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-1 text-sm text-red-500"
                          >
                            <AlertCircle className="h-4 w-4" />
                            {errors.endDate}
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </>
              )}

              {/* Loading State between KPI and SMART Analysis */}
              {currentStep === 2 && isAnalyzing && (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="h-12 w-12 text-heritage-blue animate-spin mb-4" />
                  {/* OLD: Analyzing Your Objective */}
                  <h3 className="text-xl font-semibold text-heritage-blue mb-2">Analysing Draft Objective</h3>
                  <p className="text-gray-600 text-center max-w-md">
                    Our AI-empowered "Objective Wizard" is assessing your draft against SMART criteria — optimising suggestions are being generated...
                  </p>
                </div>
              )}

              {/* Step 3: KPIs */}
              {currentStep === 2 && !isAnalyzing && (
                <>
                  <CardHeader>
                    <CardTitle className="text-2xl text-heritage-blue">Key Performance Indicators</CardTitle>
                    <CardDescription>
                      Add KPIs to measure your objective's success
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {errors.kpis && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-1 text-sm text-red-500 p-3 bg-red-50 rounded-md"
                      >
                        <AlertCircle className="h-4 w-4" />
                        {errors.kpis}
                      </motion.div>
                    )}

                    <div className="space-y-4">
                      {formData.kpis.map((kpi, index) => (
                        <motion.div
                          key={kpi.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="p-4 border rounded-lg space-y-3 relative"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-heritage-blue">KPI {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeKPI(kpi.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 space-y-2">
                              <Label htmlFor={`kpi-title-${kpi.id}`}>
                                KPI Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                id={`kpi-title-${kpi.id}`}
                                value={kpi.title}
                                onChange={(e) => updateKPI(kpi.id, 'title', e.target.value)}
                                placeholder="e.g., Student Pass Rate"
                                className={errors[`kpi_${index}_title`] ? 'border-red-500' : ''}
                              />
                              {errors[`kpi_${index}_title`] && (
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="flex items-center gap-1 text-sm text-red-500"
                                >
                                  <AlertCircle className="h-4 w-4" />
                                  {errors[`kpi_${index}_title`]}
                                </motion.div>
                              )}
                            </div>

                            <div className="col-span-2 space-y-2">
                              <Label htmlFor={`kpi-description-${kpi.id}`}>
                                Description
                              </Label>
                              <Textarea
                                id={`kpi-description-${kpi.id}`}
                                value={kpi.description}
                                onChange={(e) => updateKPI(kpi.id, 'description', e.target.value)}
                                placeholder="Brief description of this KPI"
                                rows={2}
                              />
                            </div>

                            {/* Measurement Type - drives other fields */}
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor={`kpi-mode-${kpi.id}`}>
                                  Measurement Type <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  value={kpi.measurementMode}
                                  onValueChange={(value) => {
                                    // Update mode and auto-set target/unit based on type
                                    updateKPI(kpi.id, 'measurementMode', value)
                                    if (value === 'percentage') {
                                      updateKPI(kpi.id, 'unit', '%')
                                    } else if (value === 'rating') {
                                      updateKPI(kpi.id, 'target', '100')
                                      updateKPI(kpi.id, 'unit', 'status')
                                    } else if (value === 'binary') {
                                      updateKPI(kpi.id, 'target', '100')
                                      updateKPI(kpi.id, 'unit', 'achieved')
                                    }
                                  }}
                                >
                                  <SelectTrigger id={`kpi-mode-${kpi.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="numeric">Numeric (count, amount, days)</SelectItem>
                                    <SelectItem value="percentage">Percentage (0-100%)</SelectItem>
                                    <SelectItem value="rating">Rating Scale (RAG/Status)</SelectItem>
                                    <SelectItem value="binary">Binary (Achieved/Not Achieved)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor={`kpi-frequency-${kpi.id}`}>Frequency</Label>
                                <Select
                                  value={kpi.frequency}
                                  onValueChange={(value) => updateKPI(kpi.id, 'frequency', value)}
                                >
                                  <SelectTrigger id={`kpi-frequency-${kpi.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="bi-annually">Bi-annually (6 months)</SelectItem>
                                    <SelectItem value="annually">Annually</SelectItem>
                                    <SelectItem value="quarterly">Quarterly</SelectItem>
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Target & Unit - conditional based on measurement type */}
                            {kpi.measurementMode === 'rating' ? (
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-800">
                                  <strong>Rating Scale:</strong> Progress will be measured as Red (0%), Amber (50%), or Green (100%).
                                  No numeric target needed.
                                </p>
                              </div>
                            ) : kpi.measurementMode === 'binary' ? (
                              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                <p className="text-sm text-green-800">
                                  <strong>Binary Measurement:</strong> Progress will be either "Achieved" (100%) or "Not Achieved" (0%).
                                  No numeric target needed.
                                </p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor={`kpi-target-${kpi.id}`}>
                                    Target Value <span className="text-red-500">*</span>
                                  </Label>
                                  <Input
                                    id={`kpi-target-${kpi.id}`}
                                    type={kpi.measurementMode === 'percentage' ? 'number' : 'text'}
                                    min={kpi.measurementMode === 'percentage' ? 0 : undefined}
                                    max={kpi.measurementMode === 'percentage' ? 100 : undefined}
                                    value={kpi.target}
                                    onChange={(e) => updateKPI(kpi.id, 'target', e.target.value)}
                                    placeholder={kpi.measurementMode === 'percentage' ? 'e.g., 85' : 'e.g., 7'}
                                    className={errors[`kpi_${index}_target`] ? 'border-red-500' : ''}
                                  />
                                  {errors[`kpi_${index}_target`] && (
                                    <motion.div
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      className="flex items-center gap-1 text-sm text-red-500"
                                    >
                                      <AlertCircle className="h-4 w-4" />
                                      {errors[`kpi_${index}_target`]}
                                    </motion.div>
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor={`kpi-unit-${kpi.id}`}>Unit</Label>
                                  {kpi.measurementMode === 'percentage' ? (
                                    <Input
                                      id={`kpi-unit-${kpi.id}`}
                                      value="%"
                                      disabled
                                      className="bg-gray-100"
                                    />
                                  ) : (
                                    <Input
                                      id={`kpi-unit-${kpi.id}`}
                                      value={kpi.unit}
                                      onChange={(e) => updateKPI(kpi.id, 'unit', e.target.value)}
                                      placeholder="e.g., days, count, hours"
                                    />
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Progress Direction - only for numeric/percentage */}
                            {(kpi.measurementMode === 'numeric' || kpi.measurementMode === 'percentage') && (
                              <div className="space-y-2">
                                <Label htmlFor={`kpi-direction-${kpi.id}`}>
                                  Progress Direction
                                  <span className="text-xs text-gray-500 ml-1">(How is success measured?)</span>
                                </Label>
                                <Select
                                  value={kpi.measurementDirection}
                                  onValueChange={(value) => updateKPI(kpi.id, 'measurementDirection', value)}
                                >
                                  <SelectTrigger id={`kpi-direction-${kpi.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="higher_is_better">
                                      <div className="flex items-center gap-2">
                                        <TrendingUp className="h-4 w-4 text-green-600" />
                                        Higher is Better (e.g., pass rate, satisfaction)
                                      </div>
                                    </SelectItem>
                                    <SelectItem value="lower_is_better">
                                      <div className="flex items-center gap-2">
                                        <TrendingUp className="h-4 w-4 text-blue-600 rotate-180" />
                                        Lower is Better (e.g., turnaround time, defects)
                                      </div>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={addKPI}
                      className="w-full border-dashed border-2 hover:border-heritage-blue hover:text-heritage-blue"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add KPI
                    </Button>
                  </CardContent>
                </>
              )}

              {/* Step 4: SMART Analysis */}
              {currentStep === 3 && (
                <>
                  <CardHeader>
                    <CardTitle className="text-2xl text-heritage-blue flex items-center gap-2">
                      <Sparkles className="h-6 w-6" />
                      SMART Analysis
                      <InfoTooltip text={<div><p className="mb-1.5">Objectives are assessed against five SMART criteria. Higher scores indicate better-defined objectives.</p><ul className="space-y-1"><li><strong>S</strong>pecific — Clear and well-defined</li><li><strong>M</strong>easurable — Quantifiable outcomes</li><li><strong>A</strong>chievable — Realistic and attainable</li><li><strong>R</strong>elevant — Aligned with strategic goals</li><li><strong>T</strong>ime-bound — Clear deadlines</li></ul><p className="mt-2 pt-2 border-t border-gray-600 text-gray-300 text-xs">AI-powered analysis of your objective against SMART criteria</p></div>} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-6">
                    {isAnalyzing && (
                      <div className="flex flex-col items-center justify-center py-12 space-y-4">
                        <Loader2 className="h-12 w-12 text-heritage-blue animate-spin" />
                        <div className="text-center space-y-2 max-w-md">
                          <h3 className="text-lg font-semibold text-heritage-blue">Analysing Your Objective</h3>
                          <p className="text-sm text-gray-600">
                            Our AI-empowered "Objective Wizard" is assessing your draft against SMART criteria...
                          </p>
                          <p className="text-xs text-gray-400">This may take a few seconds</p>
                        </div>
                      </div>
                    )}

                    {analysisError && !isAnalyzing && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-red-800">{analysisError}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={rerunAnalysis}
                              className="mt-2"
                            >
                              Try Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {smartAnalysis && !isAnalyzing && (
                      <>
                        {/* Overall Score */}
                        <div className="bg-gradient-to-br from-heritage-blue/5 to-heritage-blue/10 rounded-xl p-6 border border-heritage-blue/20">
                          <div className="flex items-center justify-center gap-8">
                            {/* Radial score indicator */}
                            <div className="relative h-28 w-28 flex-shrink-0">
                              <svg className="h-28 w-28 -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200" />
                                <circle
                                  cx="50" cy="50" r="42" fill="none" strokeWidth="8" strokeLinecap="round"
                                  className={smartAnalysis.overallScore >= 80 ? 'text-green-500' : smartAnalysis.overallScore >= 60 ? 'text-yellow-500' : 'text-red-500'}
                                  stroke="currentColor"
                                  strokeDasharray={`${(smartAnalysis.overallScore / 100) * 2 * Math.PI * 42} ${2 * Math.PI * 42}`}
                                />
                              </svg>
                              <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className={`text-3xl font-bold ${getScoreColor(smartAnalysis.overallScore)}`}>
                                  {smartAnalysis.overallScore}
                                </span>
                                <span className="text-[10px] text-gray-500">/ 100</span>
                              </div>
                            </div>
                            {/* Score details */}
                            <div className="text-left space-y-2">
                              <div className="text-sm font-medium text-gray-600">Overall SMART Score</div>
                              <Badge className={getScoreBgColor(smartAnalysis.overallScore)}>
                                {getScoreLabel(smartAnalysis.overallScore)}
                              </Badge>
                              <div className="flex gap-1 mt-1">
                                {['specific', 'measurable', 'achievable', 'relevant', 'timeBound'].map((k) => {
                                  const c = smartAnalysis[k as keyof typeof smartAnalysis] as { score: number } | undefined
                                  if (!c || typeof c.score !== 'number') return null
                                  return (
                                    <div key={k} className="flex flex-col items-center">
                                      <div className={`h-1.5 w-8 rounded-full ${c.score >= 80 ? 'bg-green-400' : c.score >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`} />
                                      <span className="text-[9px] text-gray-400 mt-0.5">
                                        {k === 'timeBound' ? 'T' : k.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Individual Criteria */}
                        <div className="space-y-4">
                          {Object.entries(smartAnalysis).map(([key, value]) => {
                            // Skip non-criterion fields
                            if (key === 'overallScore' || key === 'strategic_alignment' || key === 'strategic_suggestions') return null
                            
                            const criterion = value as { score: number; feedback: string; suggestions: string[] }
                            // Defensive check: ensure criterion has expected structure
                            if (!criterion || typeof criterion.score !== 'number') return null
                            
                            /* OLD: const criterionName = key === 'timeBound' ? 'Time-Bound' : key.charAt(0).toUpperCase() + key.slice(1) */
                            const criterionName = key === 'timeBound' ? 'SMART (Time-bound)' :
                                                 key === 'specific' ? 'SMART (Specific)' :
                                                 key === 'measurable' ? 'SMART (Measurable)' :
                                                 key === 'achievable' ? 'SMART (Achievable)' :
                                                 key === 'relevant' ? 'SMART (Relevant)' :
                                                 key.charAt(0).toUpperCase() + key.slice(1)
                            
                            return (
                              <motion.div
                                key={key}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`border rounded-lg border-l-4 ${getScoreBorderColor(criterion.score)} overflow-hidden`}
                              >
                                {/* Header */}
                                <div className="flex items-center justify-between p-4 pb-3">
                                  <div className="flex items-center gap-3">
                                    <div className={`h-11 w-11 rounded-full flex items-center justify-center ${getScoreBgColor(criterion.score)}`}>
                                      <span className={`text-sm font-bold ${getScoreColor(criterion.score)}`}>
                                        {criterion.score}
                                      </span>
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-heritage-blue flex items-baseline gap-0.5">
                                        {(() => {
                                          const smartLetters = 'SMART'
                                          const highlightIndex = key === 'specific' ? 0 :
                                                                key === 'measurable' ? 1 :
                                                                key === 'achievable' ? 2 :
                                                                key === 'relevant' ? 3 : 4
                                          const label = key === 'timeBound' ? 'Time-bound' :
                                                       key.charAt(0).toUpperCase() + key.slice(1)
                                          return (
                                            <>
                                              <span className="tracking-wide">
                                                {smartLetters.split('').map((letter, i) => (
                                                  <span key={i} className={i === highlightIndex ? 'text-heritage-blue font-extrabold' : 'text-gray-300'}>
                                                    {letter}
                                                  </span>
                                                ))}
                                              </span>
                                              <span className="text-gray-400 font-normal text-sm mx-0.5">—</span>
                                              <span className="text-gray-700 font-medium text-sm">{label}</span>
                                            </>
                                          )
                                        })()}
                                      </h4>
                                      <p className="text-xs text-gray-400 mt-0.5">
                                        {key === 'specific' ? 'Clear and well-defined' :
                                         key === 'measurable' ? 'Quantifiable outcomes' :
                                         key === 'achievable' ? 'Realistic and attainable' :
                                         key === 'relevant' ? 'Aligned with strategic goals' :
                                         'Clear deadlines'}
                                      </p>
                                    </div>
                                  </div>
                                  <Badge variant="outline" className={`text-xs ${criterion.score >= 80 ? 'border-green-300 text-green-700' : criterion.score >= 60 ? 'border-yellow-300 text-yellow-700' : 'border-red-300 text-red-700'}`}>
                                    {getScoreLabel(criterion.score)}
                                  </Badge>
                                </div>

                                {/* Score bar */}
                                <div className="px-4">
                                  <Progress value={criterion.score} className="h-1.5" indicatorClassName={getScoreProgressColor(criterion.score)} />
                                </div>

                                {/* Feedback */}
                                <div className="px-4 pt-3 pb-2 text-sm text-gray-700">
                                  <p>{criterion.feedback}</p>
                                </div>

                                {/* Suggestions */}
                                {criterion.suggestions && criterion.suggestions.length > 0 && (
                                  <div className="mx-4 mb-4 bg-blue-50 rounded-lg p-3 space-y-2">
                                    <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                                      <Lightbulb className="h-4 w-4" />
                                      Suggestions
                                    </div>
                                    <ul className="text-sm text-blue-800 space-y-1">
                                      {criterion.suggestions.map((suggestion, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                          <span className="text-blue-600 mt-0.5">•</span>
                                          <span>{suggestion}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </motion.div>
                            )
                          })}
                        </div>

                        {/* Rerun Analysis Button */}
                        <div className="flex justify-center">
                          <Button
                            variant="outline"
                            onClick={rerunAnalysis}
                            className="text-heritage-blue border-heritage-blue hover:bg-heritage-blue/5"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Rerun Analysis
                          </Button>
                        </div>
                      </>
                    )}

                    {!smartAnalysis && !isAnalyzing && !analysisError && (
                      <div className="text-center py-12">
                        <Sparkles className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600 mb-4">Ready to analyse your objective</p>
                        <Button
                          onClick={performSmartAnalysis}
                          className="bg-heritage-blue hover:bg-heritage-blue/90"
                        >
                          Start SMART Analysis
                        </Button>
                      </div>
                    )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </>
              )}

              {/* Step 5: Review */}
              {currentStep === 4 && (
                <>
                  <CardHeader>
                    {/* OLD: Review Your Objective */}
                    <CardTitle className="text-2xl text-heritage-blue">Reflect on Draft Objective</CardTitle>
                    <CardDescription>
                      Review and finalize your objective details before submission
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* SMART Score Summary */}
                    {smartAnalysis && (
                      <div className="p-4 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                              <Sparkles className="h-6 w-6 text-heritage-blue" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-heritage-blue">SMART Analysis Complete</h3>
                              <p className="text-sm text-gray-600">Overall Score: <span className={`font-bold ${getScoreColor(smartAnalysis.overallScore)}`}>{smartAnalysis.overallScore}/100</span></p>
                            </div>
                          </div>
                          <Badge className={getScoreBgColor(smartAnalysis.overallScore)}>
                            {getScoreLabel(smartAnalysis.overallScore)}
                          </Badge>
                        </div>
                      </div>
                    )}

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold mb-3 text-heritage-blue">Basic Information</h3>
                      <div className="space-y-2">
                        <div><span className="text-gray-600">Title:</span> <span className="font-medium">{formData.title}</span></div>
                        <div><span className="text-gray-600">Description:</span> <p className="mt-1">{formData.description}</p></div>
                        <div><span className="text-gray-600">KPA:</span> <span className="font-medium">{formData.kpaName}</span></div>
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold mb-3 text-heritage-blue">Timeline</h3>
                      <div>
                        <span className="text-gray-600">Duration:</span>{' '}
                        <span className="font-medium">{formData.startDate} to {formData.endDate}</span>
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold mb-3 text-heritage-blue">KPIs ({formData.kpis.length})</h3>
                      <div className="space-y-2">
                        {formData.kpis.map((kpi, index) => (
                          <div key={kpi.id} className="p-3 bg-white rounded border">
                            <div className="font-medium">{index + 1}. {kpi.title}</div>
                            <div className="text-sm mt-1 text-gray-600">
                              Target: {kpi.target} {kpi.unit && `(${kpi.unit})`} • Frequency: {kpi.frequency}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          <CardFooter className="flex justify-between pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={currentStep === 0 ? onCancel : prevStep}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              type="button"
              onClick={currentStep === steps.length - 1 ? handleSubmit : nextStep}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
              disabled={isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analysing...
                </>
              ) : currentStep === steps.length - 1 ? (
                <>
                  Submit <Check className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </motion.div>

      <div className="mt-4 text-center text-sm text-gray-600">
        Step {currentStep + 1} of {steps.length}
      </div>
    </div>
  )
}
